import { useQuery } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'
import { Sparkles } from 'lucide-react'
import apiClient from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type { KitStatus } from '@/types/marketingProactive'
import { MarketingShell } from '../MarketingShell'
import { dateRange, KIND_LABEL, KIND_TONE, kindIcon } from './proactiveMeta'
import { KitBuilder } from './KitBuilder'

export const KIT_STATUS_LABEL: Record<KitStatus, string> = {
  draft: 'Draft',
  launched: 'Launched',
  archived: 'Archived',
}

/** /admin/marketing/kits — the list, or one kit's builder with ?kit=<id>. */
export function MarketingKits() {
  const search = useSearch({ strict: false }) as { kit?: string }
  if (search.kit) return <KitBuilder kitId={search.kit} />
  return <KitList />
}

function KitList() {
  const query = useQuery({
    queryKey: ['marketing', 'kits'],
    queryFn: () => apiClient.listMarketingKits(),
    staleTime: 30_000,
  })
  const kits = query.data?.data?.kits ?? []

  return (
    <MarketingShell
      title="Kits"
      description="Everything one moment needs — the offer, the audience, the words, the posts and the poster — ready to launch"
    >
      {query.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-40 rounded-lg" />
          ))}
        </div>
      ) : kits.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <p className="font-medium">No kits yet</p>
            <p className="max-w-md text-sm text-muted-foreground">
              Press <span className="font-medium text-foreground">Build kit</span> on any idea in the Overview, or{' '}
              <span className="font-medium text-foreground">Plan</span> on a calendar moment, and the kit is drafted for you.
            </p>
            <div className="flex gap-2">
              <Link to="/admin/marketing/overview" className="text-sm font-medium text-primary hover:underline">
                See today's ideas →
              </Link>
              <Link to="/admin/marketing/calendar" className="text-sm font-medium text-primary hover:underline">
                Open the calendar →
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {kits.map((k) => {
            const Icon = kindIcon(k.kind)
            const photo = k.products.find((p) => p.image_url)?.image_url
            return (
              <Link key={k.id} to="/admin/marketing/kits" search={{ kit: k.id }} className="group">
                <Card className={cn('h-full overflow-hidden transition-colors group-hover:border-primary/40', k.status === 'archived' && 'opacity-60')}>
                  <div className="flex h-24 items-center justify-center bg-muted/40">
                    {photo ? (
                      <img src={photo} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className={cn('flex h-10 w-10 items-center justify-center rounded-lg', KIND_TONE[k.kind])}>
                        <Icon className="h-5 w-5" />
                      </div>
                    )}
                  </div>
                  <CardContent className="space-y-1.5 p-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{KIND_LABEL[k.kind]}</span>
                      <Badge variant={k.status === 'launched' ? 'default' : 'secondary'}>{KIT_STATUS_LABEL[k.status]}</Badge>
                    </div>
                    <p className="truncate font-medium">{k.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {dateRange(k.starts_on, k.ends_on)}
                      {k.offer_label ? ` · ${k.offer_label}` : ''}
                    </p>
                    {k.status === 'launched' && k.code && (
                      <p className="text-sm">
                        <span className="font-mono">{k.code}</span>
                        <span className="text-muted-foreground"> · used {k.redemptions}×</span>
                      </p>
                    )}
                  </CardContent>
                </Card>
              </Link>
            )
          })}
        </div>
      )}
    </MarketingShell>
  )
}
